import { useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useProjects } from '../contexts/ProjectsContext';
import { useClients } from '../contexts/ClientsContext';

export function useSupabaseProjects() {
  const { setProjects } = useProjects();
  const { clients } = useClients();

  // Fetch estimations from Supabase
  const fetchProjects = useCallback(async () => {
    if (!supabase) return;

    // Get the logged-in username from localStorage
    const username = localStorage.getItem('bidflow_username');
    if (!username) return; // Don't fetch if not logged in

    // Get the current user's ID
    const { data: { user } } = await supabase.auth.getUser();
    const SUPER_ADMIN_ID = '32e2a58f-b967-4315-9819-03c4937b8385';

    // Build query - founders and super admin can see everything, others only see their own
    let query = supabase
      .from('estimations')
      .select(`
        *,
        clients (
          name
        ),
        estimation_files (
          id,
          file_name,
          file_size,
          file_type,
          storage_path,
          file_order
        ),
        estimation_specs (
          id,
          file_name,
          file_size,
          file_type,
          storage_path,
          file_order
        )
      `);

    // Only filter by username if not founders account or super admin
    const isSuperAdmin = user?.id === SUPER_ADMIN_ID;
    if (username !== 'founders' && !isSuperAdmin) {
      query = query.eq('username', username);
    } else if (isSuperAdmin) {
      console.log('🔓 Super Admin Access: Showing all projects across the application');
    }

    const { data, error } = await query.order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching projects:', error);
      return;
    }

    if (data) {
      const formattedProjects = await Promise.all(data.map(async (p: any) => {
        // Get files from estimation_files table (new multi-file support)
        let files: Array<{
          id: string;
          name: string;
          size: number;
          type: string;
          storagePath: string;
          url: string;
        }> = [];

        if (p.estimation_files && p.estimation_files.length > 0) {
          files = p.estimation_files
            .sort((a: any, b: any) => a.file_order - b.file_order)
            .map((f: any) => {
              const { data: urlData } = supabase.storage
                .from('bucket')
                .getPublicUrl(f.storage_path);

              return {
                id: f.id,
                name: f.file_name,
                size: f.file_size || 0,
                type: f.file_type || 'application/pdf',
                storagePath: f.storage_path,
                url: urlData.publicUrl,
              };
            });
        }
        // Fallback to legacy blueprint_url field
        else if (p.blueprint_url) {
          files = [{
            id: p.id + '-file',
            name: p.blueprint_name || 'Blueprint',
            size: p.blueprint_size || 0,
            type: 'application/pdf',
            storagePath: p.blueprint_path || '',
            url: p.blueprint_url,
          }];
        }

        // Get specs from estimation_specs table
        let specs: Array<{
          id: string;
          name: string;
          size: number;
          type: string;
          storagePath: string;
          url: string;
        }> = [];

        if (p.estimation_specs && p.estimation_specs.length > 0) {
          specs = p.estimation_specs
            .sort((a: any, b: any) => a.file_order - b.file_order)
            .map((s: any) => {
              const { data: urlData } = supabase.storage
                .from('bucket')
                .getPublicUrl(s.storage_path);

              return {
                id: s.id,
                name: s.file_name,
                size: s.file_size || 0,
                type: s.file_type || 'application/pdf',
                storagePath: s.storage_path,
                url: urlData.publicUrl,
              };
            });
        }

        return {
          id: p.id,
          name: p.name,
          folderId: p.folder_id || '00000000-0000-0000-0000-000000000001',
          clientId: p.client_id || '',
          clientName: (p.clients && Array.isArray(p.clients) && p.clients.length > 0 ? p.clients[0].name : p.clients?.name) || p.client_name || '',
          description: p.description || '',
          bidDeadline: p.bid_deadline || '',
          status: p.status || 'estimating',
          projectType: p.project_type || 'commercial',
          estimatedBudget: p.estimated_budget ? parseFloat(p.estimated_budget) : 0,
          contractSize: p.contract_size ? parseFloat(p.contract_size) : undefined,
          address: '',
          city: '',
          state: '',
          zipCode: '',
          files: files.length > 0 ? files : undefined,
          specs: specs.length > 0 ? specs : undefined,
          createdAt: p.created_at,
          updatedAt: p.updated_at,
          lastOpenedAt: p.last_opened_at || undefined,
          fromSupabase: true,
        };
      }));

      setProjects(formattedProjects);
    }
  }, [setProjects]);

  useEffect(() => {
    if (!supabase) return;

    fetchProjects();

    // Subscribe to changes on estimations, estimation_files, and estimation_specs
    const subscription = supabase
      .channel('project-changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'estimations' }, (payload: any) => {
        console.log('Estimations changed:', payload);
        fetchProjects();
      })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'estimation_files' }, (payload: any) => {
        console.log('Estimation files changed:', payload);
        fetchProjects();
      })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'estimation_specs' }, (payload: any) => {
        console.log('Estimation specs changed:', payload);
        fetchProjects();
      })
      .subscribe((status: any, err?: any) => {
        if (err) {
          console.error('Subscription error:', err);
        }
        console.log('Subscription status:', status);
      });

    return () => {
      subscription.unsubscribe();
    };
  }, [fetchProjects, clients]);

  return { refetch: fetchProjects };
}

